// Incidents — operator-declared platform incidents with a timeline (scheduler-service).
// P1-6b (docs/admin-support-incidents-scope.md §5b).
import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Button, Card, Drawer, Empty, Form, Input, Modal, Select, Space, Switch, Table, Tag, Typography, message,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { selectHasPermission } from '../../features/auth/authSlice';
import { useAppSelector } from '../../app/hooks';
import { PageHeader, StatusTag, useUrlTableState, SavedViews, ExportButton, type CsvColumn } from '../../components/console';
import * as api from '../../services/incidentService';

const { Text, Paragraph } = Typography;

const SEVERITY_COLOR: Record<string, string> = { sev1: 'error', sev2: 'error', sev3: 'warning', sev4: 'default' };
const SEVERITY_OPTIONS = ['sev1', 'sev2', 'sev3', 'sev4'];
const STATUS_OPTIONS = ['investigating', 'identified', 'monitoring', 'resolved'];
const fmt = (iso?: string | null) => (iso ? fmtDateTime(iso) : '—');

const INCIDENT_EXPORT_COLUMNS: CsvColumn<api.Incident>[] = [
  { header: 'Ref', key: 'ref' },
  { header: 'Title', key: 'title' },
  { header: 'Severity', key: 'severity' },
  { header: 'Status', key: 'status' },
  { header: 'Public', value: (i) => (i.is_public ? 'yes' : 'no') },
  { header: 'Affected services', value: (i) => (i.affected_services || []).join('|') },
  { header: 'Started', value: (i) => (i.started_at ? new Date(i.started_at).toISOString() : '') },
  { header: 'Resolved', value: (i) => (i.resolved_at ? new Date(i.resolved_at).toISOString() : '') },
];

export const IncidentsPage: React.FC = () => {
  const canManage = useAppSelector(selectHasPermission('incident:manage'));
  const { filters, setFilter } = useUrlTableState(['status']);

  const [rows, setRows] = useState<api.Incident[]>([]);
  const [loading, setLoading] = useState(true);
  const [declaring, setDeclaring] = useState(false);
  const [form] = Form.useForm();
  const [selected, setSelected] = useState<{ incident: api.Incident; updates: api.IncidentUpdate[] } | null>(null);
  const [drawerBusy, setDrawerBusy] = useState(false);
  const [updateBody, setUpdateBody] = useState('');
  const [updateStatus, setUpdateStatus] = useState<string | undefined>(undefined);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api.listIncidents(filters.status || undefined));
    } catch {
      message.error('Could not load incidents.');
    } finally {
      setLoading(false);
    }
  }, [filters.status]);

  useEffect(() => { void load(); }, [load]);

  const open = async (id: string) => {
    try {
      setSelected(await api.getIncident(id));
      setUpdateBody('');
      setUpdateStatus(undefined);
    } catch {
      message.error('Could not open the incident.');
    }
  };

  const declare = async () => {
    const v = await form.validateFields();
    setDrawerBusy(true);
    try {
      const inc = await api.createIncident({
        title: v.title, severity: v.severity, summary: v.summary,
        affected_services: v.affected_services, is_public: !!v.is_public,
      });
      message.success(`Incident ${inc.ref} declared`);
      setDeclaring(false);
      form.resetFields();
      await load();
      await open(inc.id);
    } catch (e: any) {
      message.error(e?.response?.data?.error?.message || 'Could not declare incident.');
    } finally {
      setDrawerBusy(false);
    }
  };

  const postUpdate = async () => {
    if (!selected || !updateBody.trim()) return;
    setDrawerBusy(true);
    try {
      await api.addIncidentUpdate(selected.incident.id, updateBody.trim(), updateStatus);
      setUpdateBody('');
      setUpdateStatus(undefined);
      await open(selected.incident.id);
      await load();
    } catch {
      message.error('Could not post the update.');
    } finally {
      setDrawerBusy(false);
    }
  };

  const setSeverity = async (sev: string) => {
    if (!selected) return;
    await api.patchIncident(selected.incident.id, { severity: sev });
    await open(selected.incident.id);
    await load();
  };

  return (
    <div className="p-1">
      <PageHeader
        title="Incidents"
        subtitle="Platform incidents and their public/internal timeline."
        actions={
          <Space>
            <Select
              allowClear placeholder="Status" style={{ width: 150 }} value={filters.status || undefined}
              onChange={(v) => setFilter('status', v)} options={STATUS_OPTIONS.map((s) => ({ value: s, label: s }))}
            />
            <SavedViews pageKey="incidents" />
            <ExportButton filename="incidents" rows={rows} columns={INCIDENT_EXPORT_COLUMNS} />
            <Button icon={<ReloadOutlined />} onClick={() => void load()} loading={loading}>Refresh</Button>
            {canManage && <Button type="primary" icon={<PlusOutlined />} onClick={() => setDeclaring(true)}>Declare incident</Button>}
          </Space>
        }
      />

      <Card>
        <Table
          size="small"
          rowKey="id"
          loading={loading}
          dataSource={rows}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
          onRow={(r) => ({ onClick: () => void open(r.id), style: { cursor: 'pointer' } })}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No incidents." /> }}
          columns={[
            { title: 'Ref', dataIndex: 'ref', render: (r: string) => <Text code>{r}</Text> },
            { title: 'Title', dataIndex: 'title', ellipsis: true },
            { title: 'Severity', dataIndex: 'severity', render: (s: string) => <Tag color={SEVERITY_COLOR[s] || 'default'}>{s}</Tag> },
            { title: 'Status', dataIndex: 'status', render: (s: string) => <StatusTag status={s} /> },
            { title: 'Public', dataIndex: 'is_public', render: (p: boolean) => (p ? <Tag color="processing">public</Tag> : <Text type="secondary">internal</Text>) },
            { title: 'Started', dataIndex: 'started_at', render: fmt },
          ]}
        />
      </Card>

      <Modal
        title="Declare incident" open={declaring} onOk={declare} confirmLoading={drawerBusy}
        onCancel={() => setDeclaring(false)} okText="Declare"
      >
        <Form form={form} layout="vertical" initialValues={{ severity: 'sev3', is_public: false }}>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input placeholder="Short summary of the incident" />
          </Form.Item>
          <Form.Item name="severity" label="Severity">
            <Select options={SEVERITY_OPTIONS.map((s) => ({ value: s, label: s }))} />
          </Form.Item>
          <Form.Item name="summary" label="Summary">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="affected_services" label="Affected services">
            <Select mode="tags" placeholder="e.g. payroll, auth" tokenSeparators={[',']} />
          </Form.Item>
          <Form.Item name="is_public" label="Public (show on status page)" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        width={520} open={!!selected} onClose={() => setSelected(null)}
        title={selected ? `${selected.incident.ref} — ${selected.incident.title}` : ''}
      >
        {selected && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space wrap>
              <StatusTag status={selected.incident.status} />
              <Tag color={SEVERITY_COLOR[selected.incident.severity] || 'default'}>{selected.incident.severity}</Tag>
              {selected.incident.is_public && <Tag color="processing">public</Tag>}
            </Space>
            {selected.incident.summary && <Paragraph>{selected.incident.summary}</Paragraph>}
            <div><Text type="secondary">Affected</Text><div>{(selected.incident.affected_services || []).join(', ') || '—'}</div></div>
            <div><Text type="secondary">Started</Text> <span>{fmt(selected.incident.started_at)}</span></div>
            {selected.incident.resolved_at && <div><Text type="secondary">Resolved</Text> <span>{fmt(selected.incident.resolved_at)}</span></div>}

            {canManage && (
              <Card size="small" title="Severity">
                <Select
                  size="small" style={{ width: 160 }} value={selected.incident.severity} disabled={drawerBusy}
                  onChange={(v) => void setSeverity(v)} options={SEVERITY_OPTIONS.map((s) => ({ value: s, label: s }))}
                />
              </Card>
            )}

            <Card size="small" title="Timeline">
              <Space direction="vertical" style={{ width: '100%' }}>
                {selected.updates.length === 0 ? (
                  <Text type="secondary">No updates yet.</Text>
                ) : (
                  selected.updates.map((u) => (
                    <div key={u.id} style={{ borderBottom: '1px solid var(--border)', paddingBottom: 6 }}>
                      <Space size="small">
                        {u.status && <StatusTag status={u.status} />}
                        <Text className="text-xs text-muted-foreground">{u.posted_by || 'operator'} · {fmt(u.posted_at)}</Text>
                      </Space>
                      <div>{u.body}</div>
                    </div>
                  ))
                )}
                {canManage && (
                  <>
                    <Select
                      allowClear size="small" placeholder="Advance status (optional)" style={{ width: '100%' }}
                      value={updateStatus} onChange={setUpdateStatus}
                      options={STATUS_OPTIONS.map((s) => ({ value: s, label: s }))}
                    />
                    <Space.Compact style={{ width: '100%' }}>
                      <Input
                        placeholder="Post an update…" value={updateBody} disabled={drawerBusy}
                        onChange={(e) => setUpdateBody(e.target.value)} onPressEnter={() => void postUpdate()}
                      />
                      <Button type="primary" onClick={() => void postUpdate()} disabled={drawerBusy || !updateBody.trim()}>Post</Button>
                    </Space.Compact>
                  </>
                )}
              </Space>
            </Card>
          </Space>
        )}
      </Drawer>
    </div>
  );
};

export default IncidentsPage;
